import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { emitter } from '@/package';
import type {
  PCDData,
  PcdRenderingStoreActions,
  PcdRenderingStoreState,
} from '@/type';

import { usePcdCacheStore } from './pcdCacheStore';
import { usePcdFileStore } from './pcdFileStore';
import useTaskFrameStore from './taskFrameStore';

let PLAY_TIMER_ID: number = -1;

class PcdDataCollection {
  static LIMIT_SIZE_PER_PATH = 51;

  private _pcdDataListByPath: {
    [path: string]: {
      [index: string]: PCDData;
    };
  } = {};

  private _pcdCountByPath: {
    [path: string]: {
      [index: string]: number;
    };
  } = {};

  private _indexStackByPath: {
    [path: string]: number[];
  } = {};

  constructor() {
    this.init();
  }

  private ensurePathInitialized(path: string) {
    if (!this._pcdDataListByPath[path]) {
      this._pcdDataListByPath[path] = {};
      this._pcdCountByPath[path] = {};
      this._indexStackByPath[path] = [];
    }
  }

  getIndexStackByPath(path: string) {
    return this._indexStackByPath[path];
  }

  getPcdCount(path: string, index: number) {
    return this._pcdCountByPath[path]?.[index] ?? 0;
  }

  getPcdData(path: string, index: number): Nullish<PCDData> {
    return this.getPcdDataList(path)?.[index];
  }

  getPcdDataList(path: string) {
    return this._pcdDataListByPath[path];
  }

  init() {
    this._indexStackByPath = {};
    this._pcdDataListByPath = {};
    this._pcdCountByPath = {};
  }

  setPcdData(path: string, index: number, pcdFileData: PCDData) {
    this.ensurePathInitialized(path);

    const pcdData = this.getPcdData(path, index);
    const shouldUpdate = !pcdData || !pcdData.pointList.length;

    if (!shouldUpdate) return Promise.resolve();

    this._pcdDataListByPath[path][index] = pcdFileData;
    this._pcdCountByPath[path][index] = pcdFileData.pointList.length / 4;
    this._indexStackByPath[path].push(index);

    const indexStack = this._indexStackByPath[path];

    if (indexStack.length > PcdDataCollection.LIMIT_SIZE_PER_PATH) {
      const first = indexStack[0];

      this._pcdDataListByPath[path][first] = null as any;
      delete this._pcdDataListByPath[path][first];

      indexStack.shift();
    }

    return Promise.resolve();
  }
}

const INIT_STATE: PcdRenderingStoreState = {
  pcdIndex: -1,
  pcdInterval: 5,
  isPlaying: false,
  playIntervalTime: 200,
  pcdCollectionRerenderID: Date.now(),
};

const collection = new PcdDataCollection();

const usePcdRenderingStore = createWithEqualityFn<
  PcdRenderingStoreState & PcdRenderingStoreActions
>((set, get) => {
  const getPath = () => usePcdFileStore.getState().path;
  const getPcdFileStatusMapByPath = () =>
    usePcdCacheStore.getState().getPcdStatusMap(getPath());
  const getPcdPathMap = () =>
    usePcdCacheStore.getState().getPcdPathMap(getPath());
  const isFileFetching = () => useTaskFrameStore.getState().isFrameFileLoading;
  // const getWorkFrameInterval = () =>
  //   useTaskFrameStore.getState().selectedTask?.workFrameInterval ?? 1;

  const isLast = async () => {
    const { pcdIndex, pcdInterval } = get();
    const pcdPathMap = await getPcdPathMap();

    return pcdPathMap[pcdIndex + pcdInterval] == null;
  };

  const runPlay = (callback: (newIndex: number, isLast: boolean) => void) => {
    clearTimeout(PLAY_TIMER_ID);
    set({ isPlaying: true });

    const play = async () => {
      if (!get().isPlaying) {
        clearTimeout(PLAY_TIMER_ID);

        PLAY_TIMER_ID = -1;
        return Promise.resolve();
      }

      const [map, list] = await Promise.all([
        getPcdFileStatusMapByPath(),
        getPcdPathMap(),
      ]);
      const status = map[list[get().pcdIndex]];

      if (!isFileFetching() && status?.loaded) {
        callback(get().pcdIndex + get().pcdInterval, await isLast());

        PLAY_TIMER_ID = setTimeout(play, get().playIntervalTime) as any;
      } else {
        PLAY_TIMER_ID = setTimeout(play, get().playIntervalTime / 10) as any;
      }
    };

    return play();
  };

  return {
    ...INIT_STATE,

    clear() {
      collection.init();
      set({ ...INIT_STATE });
    },
    getCurrentPcdData: () => {
      return collection.getPcdData(getPath(), get().pcdIndex);
    },
    getCurrencPcdCount() {
      return collection.getPcdCount(getPath(), get().pcdIndex);
    },
    getPcdData(index) {
      return collection.getPcdData(getPath(), index);
    },
    next() {
      get().setPcdIndex(get().pcdIndex + 1);
    },
    play: callback => {
      if (get().isPlaying) return;

      runPlay(callback);
    },
    prev() {
      get().setPcdIndex(get().pcdIndex - 1);
    },
    setPcdIndex(index) {
      set({ pcdIndex: index });
    },
    setPcdData(index, pcdFileData, options = {}) {
      return collection.setPcdData(getPath(), index, pcdFileData).then(() => {
        if (!options.skipRender) {
          // TODO: 추후 이벤트로 변경
          set({
            pcdCollectionRerenderID: Date.now(),
          });
        }
      });
    },
    setPcdInterval(pcdInterval) {
      set({ pcdInterval });
    },
    setPlayIntervalTime(playIntervalTime) {
      set({ playIntervalTime });
    },
    setState(state) {
      set(state);
    },
    stop() {
      set({ isPlaying: false });
    },

    on(eventName, handler) {
      emitter.on(eventName, handler);
    },

    once(eventName, handler) {
      emitter.once(eventName, handler);
    },

    emit(eventName, payload) {
      emitter.emit(eventName, payload);
    },
  };
}, shallow);

export default usePcdRenderingStore;
